import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { Item } from "../models/item_model";
import { addToCartItem, getCartItems, updateCartItem } from "../services/api_service";
import { getCustomerId } from '../helper/global_helper';

interface CartContextType {
  cartItems: Item[];
  setCartItems: React.Dispatch<React.SetStateAction<Item[]>>;
  addToCart: (item: Item, added_quantity?: number, replaceCart?: boolean) => void;
  removeFromCart: (itemId: number) => void;
  clearCart: () => void;
  updateQuantity: (itemId: number, quantity: number) => void;
  totalAmount: number;
  setTotalAmount: React.Dispatch<React.SetStateAction<number>>; 
  loader: boolean;
  populateCart: (userId: string | number) => Promise<void>; 
}

 const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<Item[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [loader, setLoader] = useState(true);
const customerId = getCustomerId();

  useEffect(() => {
    if (customerId) {
      populateCart(customerId);
    } else {
      setLoader(false);
    }
  }, [customerId]);

  const populateCart = async (userId: string | number) => {
    try {
      setLoader(true);
      const res = await getCartItems();
      if (res?.items) {
        setTotalAmount(res?.summary?.total_amount ?? 0); 
        setCartItems(res.items);
      }
    } catch (err) {
      console.error("Failed to fetch cart items", err);
    } finally {
      setLoader(false);
    }
  };

  const addToCart = (item: Item, added_quantity: number = 1, replaceCart?: boolean) => {
    setCartItems((prevItems) => {
      const existingIndex = prevItems.findIndex((i) => i.item_id === item.item_id);

      if (existingIndex !== -1) {
        const updatedItems = [...prevItems];
        updatedItems[existingIndex].added_quantity += added_quantity;

        const cartId = updatedItems[existingIndex].cart_id; 
        if (cartId) {
          updateCartItem(cartId, item.item_id, updatedItems[existingIndex].added_quantity);
        }

        return updatedItems;
      }

      const newItem = { ...item, added_quantity };
      addToCartItem(item.item_id, added_quantity, replaceCart);

      return [...prevItems, newItem];
    });
  };

  const removeFromCart = (itemId: number) => {
    setCartItems((prev) => prev.filter((i) => i.item_id !== itemId));
  };

  const clearCart = () => setCartItems([]);

  const updateQuantity = (itemId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCartItems((prev) =>
      prev.map((i) => (i.item_id === itemId ? { ...i, quantity } : i))
    );
    if (customerId) {
    populateCart(customerId);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        setCartItems,
        addToCart,
        removeFromCart,
        clearCart,
        updateQuantity,
        totalAmount,
        setTotalAmount, 
        loader,
        populateCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Hook for using cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export default CartProvider;
